import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ClickOutside from "../../components/ClickOutside";
import { IoNotifications, IoSettingsOutline } from "react-icons/io5";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiCheck, FiX } from "react-icons/fi";
import { HiOutlineBell } from "react-icons/hi";
import { FaExclamationTriangle } from "react-icons/fa";
import { NotificationContext } from "../../context/NotificationContext";
import moment from 'moment'

const NotificationDropdown = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const { notifications, unreadCount, markAsRead, markAllAsRead, isPending, error } =
    useContext(NotificationContext);

  const handleNotificationClick = (notificationId) => {
    markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleSettingsClick = () => {
    navigate("/notification-settings");
    setDropdownOpen(false);
  };

  const renderNotificationIcon = () => (
    <div
      onClick={() => setDropdownOpen(!dropdownOpen)}
      className="cursor-pointer bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 p-2 sm:p-2.5 rounded-full transition-all duration-200 shadow-sm hover:shadow-md relative"
    >
      <IoNotifications className="text-lg sm:text-xl text-purple-600" />
      
      {isPending && (
        <span className="absolute -top-1 -right-1 flex items-center justify-center">
          <span className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin bg-white" />
        </span>
      )}
      
      {error && !isPending && (
        <span className="absolute -top-0.5 -right-[0.2rem] flex items-center justify-center">
          <FaExclamationTriangle className="text-red-500 text-base" />
        </span>
      )}
      
      {!isPending && !error && unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-[0.2rem] bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center min-w-[16px] px-0.5">
          {unreadCount > 9 ? <span className="text-[8px]">9+</span> : unreadCount}
        </span>
      )}
    </div>
  );

  return (
    <ClickOutside onClick={() => setDropdownOpen(false)} className="relative">
      <div className="relative">
        {renderNotificationIcon()}
        {dropdownOpen && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-25 z-40 sm:hidden" />
            <div className="fixed inset-x-4 top-20 mx-auto bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 sm:absolute sm:inset-x-auto sm:top-auto sm:-right-20 sm:mt-3 sm:w-96 lg:w-[28rem] xl:w-[28rem] md:right-0 max-w-sm sm:max-w-none">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-base font-semibold text-gray-900">
                  Notifications
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSettingsClick}
                    className="p-1.5 hover:bg-gray-200 rounded-full transition-colors duration-200"
                  >
                    <IoSettingsOutline className="text-xl text-gray-600" />
                  </button>
                  <button
                    onClick={() => setDropdownOpen(false)}
                    className="p-1.5 hover:bg-gray-200 rounded-full transition-colors duration-200 sm:hidden"
                  >
                    <FiX className="text-xl text-gray-600" />
                  </button>
                </div>
              </div>
              
              {notifications && notifications.length > 0 ? (
                <>
                  {unreadCount > 0 && (
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                      <button
                        onClick={handleMarkAllAsRead}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Mark all as read
                      </button>
                    </div>
                  )}
                  <div className="max-h-80 sm:max-h-96 overflow-y-auto">
                    {notifications.slice(0, 10).map((notification, index) => (
                      <div
                        key={notification.id || index}
                        className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group ${
                          !notification.isRead ? "bg-blue-50" : ""
                        }`}
                        onClick={() =>
                          handleNotificationClick(notification.id || index)
                        }
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                              !notification.isRead
                                ? "bg-blue-500"
                                : "bg-transparent"
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                <HiOutlineBell className="text-white text-sm" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="mb-1">
                                  <span className={`block text-sm font-bold ${!notification.isRead ? "text-primary" : "text-gray-700"}`}>
                                    {notification.title || "Notification"}
                                  </span>
                                  <span className={`block text-xs ${!notification.isRead ? "text-gray-900" : "text-gray-600"}`}>
                                    {typeof notification.message === 'string' ? notification.message : JSON.stringify(notification.message)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {moment(notification.createdAt).format('lll') || "Just now"}
                                </p>
                              </div>
                              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <button className="p-1 hover:bg-gray-200 rounded-full">
                                  <BsThreeDotsVertical className="text-gray-400 text-sm" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {notifications.length > 10 && (
                    <div className="px-4 py-3 bg-gray-50 text-center border-t border-gray-100">
                      <button
                        onClick={() => {
                          navigate("/notifications");
                          setDropdownOpen(false);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View all notifications ({notifications.length})
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="px-4 py-12 text-center">
                  <HiOutlineBell className="text-4xl text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm font-medium">
                    No notifications yet
                  </p>
                  <p className="text-gray-400 text-xs mt-1 leading-relaxed">
                    You'll see notifications here when they arrive
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </ClickOutside>
  );
};

export default NotificationDropdown;
